import type { QueryFunction, QueryKey } from "@tanstack/react-query";
import { assistantQueryKeys } from "@/constants/queryKeys";
import { API_BASE_URL } from "@/services";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ConversationMeta = {
  conversation_id: string;
  title: string;
  agent: string;
  created_at_ms: number;
  updated_at_ms: number;
};

export type ConversationMessageMetadata = {
  turn_id?: string;
  turn_index?: number;
  final?: boolean;
  charts?: MessageChartData[];
};

export type MessageChartData = {
  id: string;
  turn_id: string;
  chart_ref: string;
  chart_type: string;
  chart_spec: import("@/types/assistant").ChartSpec | string;
};

export type ConversationMessage = {
  role: string;
  content: string;
  metadata?: ConversationMessageMetadata | null;
};

// ─────────────────────────────────────────────────────────────────────────────
// REST API Functions
// ─────────────────────────────────────────────────────────────────────────────

const BASE_URL = `${API_BASE_URL}/ai/conversations`;

function createHeaders(authToken?: string): Record<string, string> {
  return {
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  };
}

/**
 * List all conversations
 */
export async function listConversations(authToken?: string, limit = 50): Promise<ConversationMeta[]> {
  const url = new URL(BASE_URL);
  url.searchParams.set("limit", String(limit));
  
  const resp = await fetch(url.toString(), { headers: createHeaders(authToken) });
  if (!resp.ok) throw new Error(`Failed to list conversations: ${resp.status}`);
  
  const data = await resp.json();
  const items = (data?.data ?? []) as ConversationMeta[];
  return items.sort((a, b) => b.updated_at_ms - a.updated_at_ms);
}

/**
 * Get messages for a conversation
 */
export async function getConversationMessages(
  conversationId: string,
  authToken?: string,
  limit = 50
): Promise<ConversationMessage[]> {
  const url = new URL(`${BASE_URL}/${encodeURIComponent(conversationId)}/messages`);
  url.searchParams.set("limit", String(limit));
  
  const resp = await fetch(url.toString(), { headers: createHeaders(authToken) });
  if (!resp.ok) throw new Error(`Failed to get messages: ${resp.status}`);
  
  const data = await resp.json();
  return (data?.data ?? []) as ConversationMessage[];
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  conversationId: string,
  authToken?: string
): Promise<{ success: boolean }> {
  const url = new URL(`${BASE_URL}/${encodeURIComponent(conversationId)}`);
  const resp = await fetch(url.toString(), { method: "DELETE", headers: createHeaders(authToken) });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Failed to delete conversation: ${resp.status}`);
  }
  return resp.json();
}

/**
 * Get usage stats
 */
export async function getUsage(authToken?: string): Promise<{ aiCalls: number }> {
  const url = new URL(`${BASE_URL}/usage`);
  const resp = await fetch(url.toString(), { headers: createHeaders(authToken) });
  if (!resp.ok) throw new Error(`Failed to get usage: ${resp.status}`);
  return resp.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// React Query Options
// ─────────────────────────────────────────────────────────────────────────────

export const conversationsQueryOptions = (authToken?: string, limit: number = 50) => ({
  queryKey: assistantQueryKeys.conversations.list(limit),
  queryFn: (async () => {
    const list = await listConversations(authToken, limit);
    return { data: list } as { data: ConversationMeta[] };
  }) as QueryFunction<{ data: ConversationMeta[] }, QueryKey>,
  enabled: Boolean(authToken),
});

export const conversationMessagesQueryOptions = (
  conversationId: string | undefined,
  authToken?: string,
  limit: number = 50
) => ({
  queryKey: assistantQueryKeys.messages(conversationId || "", limit),
  queryFn: (async () => {
    if (!conversationId) return { data: [] as ConversationMessage[] };
    const msgs = await getConversationMessages(conversationId, authToken, limit);
    return { data: msgs } as { data: ConversationMessage[] };
  }) as QueryFunction<{ data: ConversationMessage[] }, QueryKey>,
  enabled: Boolean(authToken && conversationId),
});

export const usageQueryOptions = (authToken?: string) => ({
  queryKey: assistantQueryKeys.usage(),
  queryFn: (async () => {
    const usage = await getUsage(authToken);
    return usage as { aiCalls: number };
  }) as QueryFunction<{ aiCalls: number }, QueryKey>,
  enabled: Boolean(authToken),
});
